import type {
  BloomDeviceRegisterResponse,
  BloomIntegrationMode,
  NativePushRegistration,
} from '@apm/contracts';
import type {
  PushAdapter,
  PushPermission,
  PushSubscribeResult,
  Tv2mSdk,
  WebPushSubscription,
} from '@apm/tv2m-client-sdk';

/**
 * Push 구독의 주인 — **모드가 정하는 유일한 것이다** (`docs/12` §2.4 의 표).
 *
 * - `federated` → BLOOM Server. 구독·VAPID·발송이 전부 서비스 사업자 쪽이다
 * - `delegated` → TV2M. v7 현행 그대로 (Mode A)
 *
 * 나머지는 두 모드가 완전히 같다 — 기기 등록도, 가구 그래프도, 캐스케이드도, 기록도.
 * **모드는 배포 설정이지 서로 다른 코드가 아니다** (절대 규칙 17).
 */
export type SubscriptionOwner = 'fast' | 'tv2m';

export function subscriptionOwner(mode: BloomIntegrationMode): SubscriptionOwner {
  return mode === 'federated' ? 'fast' : 'tv2m';
}

/**
 * 부팅 때 구독을 어떻게 할 것인가 (WB-15·16, 절대 규칙 19).
 *
 * - `skip` — 할 수 있는 일이 없다 (권한 없음·미지원·서버가 키를 안 줌)
 * - `none` — 붙일 구독이 없다. 사용자가 알림을 켠 적이 없거나 브라우저가 구독을 버렸다
 * - `reuse` — 키도 주인도 그대로다. 있는 구독을 그대로 다시 등록한다
 * - `resubscribe` — **키가 다르거나 모드가 바뀌었다.** 지우고 새 키로 다시 만든다
 */
export type ReconcilePlan =
  | { kind: 'skip'; reason: 'unsupported' | 'permission_denied' | 'no_vapid_key' }
  | { kind: 'none' }
  | { kind: 'reuse' }
  /** 이전 주인에게서도 구독을 떼야 하면 그 서버가 여기 있다 (WB-16) */
  | { kind: 'resubscribe'; unregisterFrom: SubscriptionOwner | null };

export interface ReconcileInput {
  mode: BloomIntegrationMode;
  vapidPublicKey: string;
  permission: PushPermission;
  supported: boolean;
  /** 브라우저가 지금 구독을 들고 있는가 */
  hasSubscription: boolean;
  /** 우리가 적어 둔 것 (`key-store.ts`) */
  stored: { key: string | null; owner: SubscriptionOwner | null };
}

/**
 * 부팅 때의 판정. **순수 함수다** — 이 규칙이 틀리면 알림이 조용히 오지 않으므로,
 * 브라우저 없이 케이스마다 확인할 수 있어야 한다.
 *
 * ── 왜 「모르면 다시 구독한다」인가 ───────────────────────────
 *
 * 브라우저의 구독 객체는 **어느 키로 만들어졌는지 알려 주지 않는다**(`key-store.ts`).
 * 그래서 적어 둔 키가 없으면 대조할 수 없고, 대조하지 못한 구독은 죽어 있을 수 있다.
 * 재구독의 대가는 왕복 한 번이고, 틀렸을 때의 대가는 **아무 증상 없이 알림이 오지 않는
 * 것**이다 (절대 규칙 19). 값이 다르므로 모르면 다시 만든다.
 */
export function planReconcile(input: ReconcileInput): ReconcilePlan {
  if (!input.supported) return { kind: 'skip', reason: 'unsupported' };
  if (input.permission !== 'granted') return { kind: 'skip', reason: 'permission_denied' };
  if (input.vapidPublicKey === '') return { kind: 'skip', reason: 'no_vapid_key' };

  const owner = subscriptionOwner(input.mode);

  // 브라우저가 구독을 버렸다(사이트 데이터 삭제·만료). **적어 둔 것도 버린다** —
  // 남겨 두면 다음 부팅이 있지도 않은 구독을 재사용한 것으로 친다.
  if (!input.hasSubscription) return { kind: 'none' };

  // **모드가 바뀌었다** (WB-16). 새 주인의 키로 다시 만들고, 이전 주인에게서도 뗀다 —
  // 안 떼면 그 서버는 살아 있는 줄 알고 계속 보내다가 410 을 받을 때까지 시도한다.
  if (input.stored.owner !== null && input.stored.owner !== owner) {
    return { kind: 'resubscribe', unregisterFrom: input.stored.owner };
  }

  // 키가 다르거나 모른다 (WB-15). 같은 주인이므로 재등록이 덮어쓴다 — 뗄 것은 없다.
  if (input.stored.key !== input.vapidPublicKey) {
    return { kind: 'resubscribe', unregisterFrom: null };
  }

  return { kind: 'reuse' };
}

/**
 * 이 기기에 알림을 보내는 **수단** (v12, 절대 규칙 38).
 *
 * **주인과 다른 축이다.** 주인은 모드가 정하고(위 `SubscriptionOwner`) 수단은 **기기가**
 * 정한다 — 셸 안이면 APNs 이고 브라우저면 Web Push 다. 둘을 한 축으로 뭉치면 「iOS 는
 * 다른 모드」가 되어 `BLOOM_INTEGRATION_MODE` 가 기기별로 갈려야 하는데, 그것이 정확히
 * 규칙 17·38 이 막는 것이다.
 *
 * 그래서 **한 매칭의 대상에 web-push 폰과 APNs 폰이 섞여 있어도 한 번의 발송으로 끝난다**
 * (`docs/29` §13.1) — 서버는 `BloomDevice.pushProvider` 한 칸으로 가른다.
 */
export type PushMeans =
  | { kind: 'webpush'; subscription: WebPushSubscription; subscribedWithKey: string }
  | { kind: 'native'; nativePush: NativePushRegistration };

/** 구독을 붙이는 데 필요한 것들. 화면이 아니라 훅이 채운다 */
export interface SubscriptionDeps {
  mode: BloomIntegrationMode;
  /** 이 배포의 VAPID 공개키. 모드에 따라 BLOOM Server 것이거나 TV2M 것이다 */
  vapidPublicKey: string;
  push: PushAdapter;
  sdk: Tv2mSdk;
  /** BLOOM Server 에 수단을 붙인다. federated 에서만 불린다 */
  registerWithBloom: (means: PushMeans) => Promise<BloomDeviceRegisterResponse>;
  /**
   * 네이티브 셸의 Push 등록을 받아 온다. **`null` 이면 웹 구독 경로다** (브라우저).
   *
   * 함수로 받는 이유는 이 모듈이 브리지를 몰라도 되게 하려는 것이다 — 여기서 재는 것은
   * 「어느 수단으로 가는가」이고, 그 수단이 어떻게 생겼는지는 훅의 사정이다.
   */
  requestNativePush: (() => Promise<NativePushRegistration | null>) | null;
}

/**
 * 구독을 만들어 **주인 쪽 서버에** 붙인다.
 *
 * ── 왜 SDK 의 `registerPushSubscription()` 하나로 끝나지 않는가 ─
 *
 * 그 메서드는 TV2M 에 붙인다. federated 에서 구독의 주인은 BLOOM Server 이므로 붙일 곳이 다르고,
 * SDK 는 그 경로를 알 수 없다 — 파트너 서버의 주소도 인증도 파트너의 사정이다.
 * 그래서 **주인을 고르는 판단만 여기 두고**, 각 경로의 실제 등록은 원래 있던 곳
 * (SDK / `bloom-device.ts`)이 그대로 한다.
 *
 * 결과 모양은 하나다(`PushSubscribeResult`) — 화면이 안내를 한 곳에서 고른다.
 *
 * **권한을 요청하지 않는다** (WB-07). 그것은 사용자 제스처를 아는 화면의 일이고,
 * 이 함수는 그 직후에 불린다.
 */
export async function subscribeWithOwner(deps: SubscriptionDeps): Promise<PushSubscribeResult> {
  if (subscriptionOwner(deps.mode) === 'tv2m') {
    // 위탁형은 v7 경로 그대로다. 여기서 흉내 내면 같은 일을 하는 코드가 두 벌이 된다.
    return deps.sdk.registerPushSubscription();
  }

  // **수단이 갈리는 자리** (규칙 38). 주인은 위에서 이미 정해졌고 여기서는 무엇으로
  // 보낼지만 고른다 — 셸이면 APNs, 브라우저면 Web Push.
  if (deps.requestNativePush !== null) return subscribeNative(deps, deps.requestNativePush);

  const permission = deps.push.getPermission();
  if (!deps.push.isSupported()) return { subscribed: false, permission, reason: 'unsupported' };
  if (permission !== 'granted') {
    return { subscribed: false, permission, reason: 'permission_denied' };
  }
  if (deps.vapidPublicKey === '') {
    // 서버가 키를 안 준 배포다. **여기서 멈추는 것이 맞다** — 키 없이 만든 구독은
    // 아무도 발송할 수 없고, 만들어 두면 브라우저에 죽은 구독만 남는다.
    return { subscribed: false, permission, reason: 'no_vapid_key' };
  }

  let subscription: WebPushSubscription;
  try {
    subscription = await deps.push.subscribe(deps.vapidPublicKey);
  } catch {
    return { subscribed: false, permission, reason: 'subscribe_failed' };
  }

  let result: BloomDeviceRegisterResponse;
  try {
    // **구독에 쓴 키를 함께 보낸다** (절대 규칙 19). 서버가 자기 VAPID 공개키와 대조해
    // 어긋나면 받지 않는다 — 어긋난 구독을 저장하면 발송이 401/403 으로 실패하는데
    // 그 실패는 발송 시점에야 드러난다.
    result = await deps.registerWithBloom({
      kind: 'webpush',
      subscription,
      subscribedWithKey: deps.vapidPublicKey,
    });
  } catch {
    return { subscribed: false, permission, reason: 'server_error' };
  }

  return fromRegisterResponse(result, permission);
}

/**
 * 셸의 APNs 등록을 받아 BLOOM Server 에 붙인다 (v12).
 *
 * ── 왜 `PushAdapter` 를 구현하지 않았는가 ──────────────────────
 *
 * `docs/29` §5 는 `NativePushAdapter` 가 그 인터페이스를 구현하라고 적었는데,
 * `PushAdapter.subscribe()` 는 **`WebPushSubscription`**(endpoint + p256dh/auth)을
 * 돌려줘야 한다. APNs 에는 그런 것이 없어 구현하려면 **가짜 endpoint 를 지어내야 하고**,
 * 그 값이 그대로 `BloomDevice.pushEndpoint` 에 저장된다 — 인터페이스를 지키지 못하는
 * 클래스는 「눌리는데 아무 일도 안 일어나는 버튼」과 같은 종류의 거짓말이다.
 *
 * 대신 **이미 있는 갈림에 수단 축을 더했다** (위 `PushMeans`). 얻은 것이 하나 더 있다 —
 * 웹 경로의 어느 줄도 바뀌지 않는다.
 *
 * ── 규칙 19 는 어디로 갔는가 ──────────────────────────────────
 *
 * VAPID 키 대조가 여기 없는 이유는 APNs 에 그 개념이 없기 때문이다. 대응하는 것은
 * **토큰 갱신**이고(D11), 그 대조는 부팅마다 셸에게 현재 토큰을 물어 서버에 다시 실어
 * 보내는 것으로 한다 (`useTv2m` 의 `reconcileSubscription`). 어겼을 때의 증상은 같다:
 * **알림만 조용히 오지 않는다.**
 */
async function subscribeNative(
  deps: SubscriptionDeps,
  requestNativePush: () => Promise<NativePushRegistration | null>,
): Promise<PushSubscribeResult> {
  const nativePush = await requestNativePush();
  if (nativePush === null) {
    // **거부와 등록 실패를 나누지 않는다** (`bridge.ts` 의 `requestPush` 주석).
    // iOS 의 거부는 되돌릴 수 없어 사용자가 할 일이 설정 앱 하나로 같고, 나눠 봐야
    // 화면이 다른 안내를 할 수 없다.
    return { subscribed: false, permission: 'denied', reason: 'permission_denied' };
  }

  let result: BloomDeviceRegisterResponse;
  try {
    result = await deps.registerWithBloom({ kind: 'native', nativePush });
  } catch {
    return { subscribed: false, permission: 'granted', reason: 'server_error' };
  }

  // 권한은 `granted` 다 — 토큰을 받았다는 것이 곧 허용됐다는 뜻이고, APNs 는 그 둘을
  // 나누지 않는다.
  return fromRegisterResponse(result, 'granted');
}

/**
 * 서버 응답을 화면이 읽을 결과로 옮긴다. **두 수단이 같은 판정을 쓴다.**
 *
 * 서버가 받지 않았을 때 **권한으로 추정하지 않는다** — `granted` 인데도 구독이 없는
 * 상태이고, 그 이유를 화면이 말해야 한다. 특히 `key_mismatch` 는 규칙 19 가 말하는
 * 그 침묵이라 여기서 이름을 붙여 내보낸다.
 */
function fromRegisterResponse(
  result: BloomDeviceRegisterResponse,
  permission: PushPermission,
): PushSubscribeResult {
  if (result.pushReady) return { subscribed: true, permission };
  return {
    subscribed: false,
    permission,
    reason: result.reason === 'key_mismatch' ? 'key_mismatch' : 'server_error',
  };
}
